import { zodResolver } from "@hookform/resolvers/zod";
import {
  useForm,
  UseFormRegister,
  UseFormHandleSubmit,
  FormState,
  FieldValues,
  DefaultValues,
  Control,
  UseFormSetValue,
} from "react-hook-form";
import { ZodType } from "zod";

// Input props
export type UseCustomReactHookFormProps<T extends FieldValues> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formSchema: ZodType<T, any>;
  formDefaultValue: DefaultValues<T>;
};

// Return types
export type UseCustomReactHookFormReturn<T extends FieldValues> = {
  register: UseFormRegister<T>;
  handleSubmit: UseFormHandleSubmit<T>;
  control: Control<T>;
  errors: FormState<T>["errors"];
  isDirty: FormState<T>["isDirty"];
  reset: () => void;
  setValue: UseFormSetValue<T>;
  getValues: () => T;
  isSubmitting: boolean;
  isValid: boolean;
};

// Hook implementation
const useCustomReactHookForm = <T extends FieldValues>({
  formSchema,
  formDefaultValue,
}: UseCustomReactHookFormProps<T>): UseCustomReactHookFormReturn<T> => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty, isSubmitting, isValid },
  } = useForm<T>({
    defaultValues: formDefaultValue,
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  return {
    control,
    register,
    handleSubmit,
    errors,
    isDirty,
    reset,
    setValue,
    getValues,
    isSubmitting,
    isValid,
  };
};

export default useCustomReactHookForm;
